import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRE_BUILD_STRATEGIES = [
  'Bear Put Spread',
  'Bull Call Spread',
  'Iron Condor',
  'Straddle',
  'Strangle',
  'Butterfly Spread'
];

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const OptionWizardTab = () => {
  const [activeWizardTab, setActiveWizardTab] = useState('prebuild');
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [underlying, setUnderlying] = useState('NIFTY 50 (NFO)');
  const [capital, setCapital] = useState('100000');
  const [strategyType, setStrategyType] = useState('Positional');
  
  // Positions
  const [positions, setPositions] = useState([
    {
      segment: 'B',
      bs: 'Select',
      strikeSelection: 'ATM Spot',
      strike: 'ATM',
      expiry: 'Current Week',
      type: 'PE',
      lots: '1',
      tgtPercent: '0',
      slPercent: '0',
      trl: '0'
    },
    {
      segment: 'S',
      bs: 'Select',
      strikeSelection: 'ATM Spot',
      strike: 'OTM',
      expiry: 'Current Week',
      type: 'PE',
      lots: '1',
      tgtPercent: '0',
      slPercent: '0',
      trl: '0'
    }
  ]);

  // Entry Settings
  const [entryHour, setEntryHour] = useState('09');
  const [entryMinute, setEntryMinute] = useState('15');
  const [entryDays, setEntryDays] = useState({
    Mon: true,
    Tue: true,
    Wed: true,
    Thu: true,
    Fri: true
  });

  // Exit Settings
  const [profitMTM, setProfitMTM] = useState('None');
  const [profitMTMValue, setProfitMTMValue] = useState('0');
  const [stoplossMTM, setStoplossMTM] = useState('None');
  const [stoplossMTMValue, setStoplossMTMValue] = useState('0');
  
  const [trailingStoploss, setTrailingStoploss] = useState('Amount');
  const [activateAt, setActivateAt] = useState('1000');
  const [lockProfitAt, setLockProfitAt] = useState('100');
  const [profitIncrease, setProfitIncrease] = useState('1');
  const [increaseTSL, setIncreaseTSL] = useState('1');
  
  const [exitHour, setExitHour] = useState('15');
  const [exitMinute, setExitMinute] = useState('10');
  const [exitOnExpiry, setExitOnExpiry] = useState('Yes');
  const [exitAfterDays, setExitAfterDays] = useState('None');

  const addPosition = () => {
    setPositions([...positions, {
      segment: 'Select',
      bs: 'Select',
      strikeSelection: 'Select',
      strike: '',
      expiry: 'Select',
      type: 'Select',
      lots: '1',
      tgtPercent: '0',
      slPercent: '0',
      trl: '0'
    }]);
  };

  const removePosition = (index) => {
    if (positions.length > 1) {
      setPositions(positions.filter((_, i) => i !== index));
    }
  };

  const updatePosition = (index, field, value) => {
    const newPositions = [...positions];
    newPositions[index][field] = value;
    setPositions(newPositions);
  };

  const handleSaveStrategy = async () => {
    try {
      // Build strategy object
      const strategyData = {
        name: selectedStrategy || 'Custom Options Strategy',
        description: `Options strategy with ${positions.length} legs`,
        strategy_set: {
          entry: {
            description: `Entry at ${entryHour}:${entryMinute}`,
            conditions_text: `Entry Time: ${entryHour}:${entryMinute}\nDays: ${Object.keys(entryDays).filter(d => entryDays[d]).join(', ')}`
          },
          entry_positions: positions.map(p => ({
            buy_sell: p.bs === 'Select' ? 'Buy' : p.bs,
            underlying: underlying.split(' ')[0],
            strike: p.strike,
            type: p.type === 'Select' ? 'CE' : p.type,
            expiry: p.expiry,
            qty: parseInt(p.lots) || 1,
            price: 'Market'
          })),
          repair: null,
          repair_positions: [],
          overnight_protection: null,
          overnight_positions: [],
          universal_exit: {
            description: 'Exit conditions',
            conditions_text: `Exit Time: ${exitHour}:${exitMinute}\nProfit: ${profitMTMValue}\nStop Loss: ${stoplossMTMValue}`
          }
        },
        advanced_settings: {
          capital_required: parseInt(capital) || 100000,
          check_conditions_every: '1 minute',
          trailing_stop_loss_activate: parseInt(activateAt) || 0,
          trailing_stop_loss_profit_increase: parseInt(profitIncrease) || 0,
          trailing_stop_loss_increase_by: parseInt(increaseTSL) || 0
        }
      };

      await axios.post(`${API}/strategies`, strategyData, { withCredentials: true });
      toast.success('Strategy created successfully!');
    } catch (error) {
      toast.error('Failed to create strategy');
    }
  };

  return (
    <Card className="p-8 bg-white/80 backdrop-blur-sm border-gray-200" data-testid="option-wizard-tab">
      <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        Option Wizard
      </h2>

      <Tabs value={activeWizardTab} onValueChange={setActiveWizardTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="prebuild" data-testid="prebuild-tab">Pre Build Strategies</TabsTrigger>
          <TabsTrigger value="create" data-testid="create-tab">Create</TabsTrigger>
          <TabsTrigger value="mystrategy" data-testid="mystrategy-tab">My Strategies</TabsTrigger>
        </TabsList>

        <TabsContent value="prebuild" className="space-y-6">
          {/* Strategy Selection */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="mb-2 block">Strategy</Label>
              <Select value={selectedStrategy} onValueChange={setSelectedStrategy}>
                <SelectTrigger data-testid="strategy-select">
                  <SelectValue placeholder="Select Strategy" />
                </SelectTrigger>
                <SelectContent>
                  {PRE_BUILD_STRATEGIES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Underlying</Label>
              <Select value={underlying} onValueChange={setUnderlying}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NIFTY 50 (NFO)">NIFTY 50 (NFO)</SelectItem>
                  <SelectItem value="BANKNIFTY (NFO)">BANKNIFTY (NFO)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="mb-2 block">Capital</Label>
              <Input 
                type="number" 
                value={capital} 
                onChange={(e) => setCapital(e.target.value)}
                data-testid="capital-input"
              />
            </div>

            <div>
              <Label className="mb-2 block">Type</Label>
              <Select value={strategyType} onValueChange={setStrategyType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Positional">Positional</SelectItem>
                  <SelectItem value="Intraday">Intraday</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Positions Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Positions</h3>
              <Button onClick={addPosition} size="sm" variant="outline" data-testid="add-position-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-2">Segment</th>
                    <th className="text-left py-2 px-2">B/S</th>
                    <th className="text-left py-2 px-2">Strike Selection</th>
                    <th className="text-left py-2 px-2">Value</th>
                    <th className="text-left py-2 px-2">Expiry</th>
                    <th className="text-left py-2 px-2">Lots(1 lot=75)</th>
                    <th className="text-left py-2 px-2">TGT</th>
                    <th className="text-left py-2 px-2">% Entry Price</th>
                    <th className="text-left py-2 px-2">SL</th>
                    <th className="text-left py-2 px-2">% Entry Price</th>
                    <th className="text-left py-2 px-2">TRL</th>
                    <th className="text-left py-2 px-2">Entry %</th>
                    <th className="py-2 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((position, index) => (
                    <tr key={index} className="border-b border-gray-100">
                      <td className="py-2 px-2">
                        <div className={`w-8 h-8 rounded flex items-center justify-center text-white font-semibold ${
                          position.segment === 'B' ? 'bg-green-500' : position.segment === 'S' ? 'bg-red-500' : 'bg-gray-400'
                        }`}>
                          {position.segment}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <Select value={position.bs} onValueChange={(v) => updatePosition(index, 'bs', v)}>
                          <SelectTrigger className="w-24 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Select">Select</SelectItem>
                            <SelectItem value="Buy">Buy</SelectItem>
                            <SelectItem value="Sell">Sell</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-2">
                        <Select value={position.strikeSelection} onValueChange={(v) => updatePosition(index, 'strikeSelection', v)}>
                          <SelectTrigger className="w-32 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ATM Spot">ATM Spot</SelectItem>
                            <SelectItem value="OTM">OTM</SelectItem>
                            <SelectItem value="ITM">ITM</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-2">
                        <Select value={position.strike} onValueChange={(v) => updatePosition(index, 'strike', v)}>
                          <SelectTrigger className="w-24 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ATM">ATM</SelectItem>
                            <SelectItem value="OTM">OTM</SelectItem>
                            <SelectItem value="ITM">ITM</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-2">
                        <Select value={position.expiry} onValueChange={(v) => updatePosition(index, 'expiry', v)}>
                          <SelectTrigger className="w-36 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Current Week">Current Week</SelectItem>
                            <SelectItem value="Next Week">Next Week</SelectItem>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-2">
                        <Select value={position.type} onValueChange={(v) => updatePosition(index, 'type', v)}>
                          <SelectTrigger className="w-20 h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="PE">PE</SelectItem>
                            <SelectItem value="CE">CE</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-2 px-2">
                        <Input 
                          type="number" 
                          value={position.lots} 
                          onChange={(e) => updatePosition(index, 'lots', e.target.value)}
                          className="w-16 h-9"
                        />
                      </td>
                      <td className="py-2 px-2">
                        <Input 
                          type="number" 
                          value={position.tgtPercent} 
                          onChange={(e) => updatePosition(index, 'tgtPercent', e.target.value)}
                          className="w-16 h-9"
                        />
                      </td>
                      <td className="py-2 px-2">
                        <Input 
                          type="number" 
                          value={position.slPercent} 
                          onChange={(e) => updatePosition(index, 'slPercent', e.target.value)}
                          className="w-16 h-9"
                        />
                      </td>
                      <td className="py-2 px-2">
                        <Input 
                          type="number" 
                          value="0" 
                          className="w-16 h-9"
                          disabled
                        />
                      </td>
                      <td className="py-2 px-2">
                        <Input 
                          type="number" 
                          value={position.trl} 
                          onChange={(e) => updatePosition(index, 'trl', e.target.value)}
                          className="w-16 h-9"
                        />
                      </td>
                      <td className="py-2 px-2">
                        <Input 
                          type="number" 
                          value="0" 
                          className="w-16 h-9"
                          disabled
                        />
                      </td>
                      <td className="py-2 px-2">
                        <Button 
                          onClick={() => removePosition(index)} 
                          variant="ghost" 
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Entry Setting */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Entry Setting</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">Entry Time (hh:mm)</Label>
                <div className="flex gap-2">
                  <Select value={entryHour} onValueChange={setEntryHour}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')}>
                          {String(i).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={entryMinute} onValueChange={setEntryMinute}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['00', '15', '30', '45'].map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Enter on Days</Label>
                <div className="flex flex-wrap gap-3 border border-gray-200 rounded-lg p-3">
                  {DAYS.map(day => (
                    <div key={day} className="flex items-center gap-2 bg-slate-100 px-3 py-1 rounded">
                      <span className="text-sm font-medium">{day}</span>
                      <button
                        onClick={() => setEntryDays({ ...entryDays, [day]: !entryDays[day] })}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Exit Setting */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Exit Setting</h3>
            <div className="space-y-6">
              {/* Profit and Stoploss MTM */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="mb-2 block">Profit MTM</Label>
                  <div className="flex gap-2">
                    <Select value={profitMTM} onValueChange={setProfitMTM}>
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None</SelectItem>
                        <SelectItem value="Amount">Amount</SelectItem>
                        <SelectItem value="Percentage">Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input 
                      type="number" 
                      value={profitMTMValue} 
                      onChange={(e) => setProfitMTMValue(e.target.value)}
                      className="w-32"
                      disabled={profitMTM === 'None'}
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Stoploss MTM</Label>
                  <div className="flex gap-2">
                    <Select value={stoplossMTM} onValueChange={setStoplossMTM}>
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="None">None</SelectItem>
                        <SelectItem value="Amount">Amount</SelectItem>
                        <SelectItem value="Percentage">Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input 
                      type="number" 
                      value={stoplossMTMValue} 
                      onChange={(e) => setStoplossMTMValue(e.target.value)}
                      className="w-32"
                      disabled={stoplossMTM === 'None'}
                    />
                  </div>
                </div>
              </div>

              {/* Trailing Stoploss */}
              <div>
                <Label className="mb-2 block">Trailing Stoploss</Label>
                <div className="grid grid-cols-5 gap-3">
                  <Select value={trailingStoploss} onValueChange={setTrailingStoploss}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Amount">Amount</SelectItem>
                      <SelectItem value="Percentage">Percentage</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div>
                    <Label className="text-xs mb-1 block">Activate At</Label>
                    <Input 
                      type="number" 
                      value={activateAt} 
                      onChange={(e) => setActivateAt(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs mb-1 block">Lock Profit At</Label>
                    <Input 
                      type="number" 
                      value={lockProfitAt} 
                      onChange={(e) => setLockProfitAt(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs mb-1 block">When Profit increase by</Label>
                    <Input 
                      type="number" 
                      value={profitIncrease} 
                      onChange={(e) => setProfitIncrease(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs mb-1 block">Increase TSL by</Label>
                    <Input 
                      type="number" 
                      value={increaseTSL} 
                      onChange={(e) => setIncreaseTSL(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Exit Time and Options */}
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <Label className="mb-2 block">Exit Time (hh:mm)</Label>
                  <div className="flex gap-2">
                    <Select value={exitHour} onValueChange={setExitHour}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i).padStart(2, '0')}>
                            {String(i).padStart(2, '0')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={exitMinute} onValueChange={setExitMinute}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['00', '10', '15', '30', '45'].map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Label>Exit On Expiry</Label>
                    <Info className="w-4 h-4 text-gray-400" />
                  </div>
                  <Select value={exitOnExpiry} onValueChange={setExitOnExpiry}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-2 block">Exit after Entry + x days</Label>
                  <Select value={exitAfterDays} onValueChange={setExitAfterDays}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="None">None</SelectItem>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="2">2 days</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="5">5 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSaveStrategy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-wizard-strategy">
              Save Strategy
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="create" className="text-center py-12">
          <Button 
            variant="outline" 
            size="lg"
            className="mx-auto"
            data-testid="create-own-strategy-btn"
          >
            Create Own Strategy
          </Button>
          <p className="text-sm text-gray-500 mt-4">Build your custom options strategy from scratch</p>
        </TabsContent>

        <TabsContent value="mystrategy" className="text-center py-12">
          <Select>
            <SelectTrigger className="max-w-md mx-auto">
              <SelectValue placeholder="Select Own Strategy" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="strategy1">My Strategy 1</SelectItem>
              <SelectItem value="strategy2">My Strategy 2</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-gray-500 mt-4">Load your saved strategies</p>
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default OptionWizardTab;
